import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper: build auth headers from localStorage
  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : {};
  }, []);

  // Helper: refresh access token. The refresh token lives in an httpOnly
  // cookie scoped to /api/auth — send no body and rely on credentials:include
  // so the browser attaches the cookie.
  const refreshAccessToken = useCallback(async () => {
    try {
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return false;
      const data = await res.json();
      localStorage.setItem("access_token", data.access_token);
      return true;
    } catch {
      return false;
    }
  }, []);

  // authedFetch: fetch with auto-retry on 401.
  // For FormData bodies (e.g. multipart logo upload) the browser must set the
  // Content-Type (with its boundary) itself, so we drop the default JSON
  // Content-Type and send only the Authorization header in that case.
  const buildHeaders = useCallback((options) => {
    const base = authHeaders();
    if (options.body instanceof FormData) {
      const { "Content-Type": _omit, ...rest } = base;
      return { ...rest, ...options.headers };
    }
    return { ...base, ...options.headers };
  }, [authHeaders]);

  const authedFetch = useCallback(async (url, options = {}) => {
    let res = await fetch(url, { ...options, headers: buildHeaders(options) });
    if (res.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        res = await fetch(url, { ...options, headers: buildHeaders(options) });
      } else {
        // Both tokens expired — force logout
        localStorage.removeItem("access_token");
        setUser(null);
      }
    }
    return res;
  }, [buildHeaders, refreshAccessToken]);

  // Login: store the access token, fetch user profile. The refresh token is
  // set server-side as an httpOnly cookie, so the refreshToken arg (kept for
  // caller compatibility) is intentionally ignored.
  const login = useCallback(async (accessToken, refreshToken) => {
    localStorage.setItem("access_token", accessToken);
    try {
      const res = await fetch("/api/auth/me", {
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      });
      if (res.ok) {
        setUser(await res.json());
      }
    } catch { /* silent */ }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try { await authedFetch("/api/auth/logout", { method: "POST" }); } catch {}
    localStorage.removeItem("access_token");
    setUser(null);
  }, [authedFetch]);

  // Silent refresh on mount
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) { setLoading(false); return; }

    fetch("/api/auth/me", { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } })
      .then(async (res) => {
        if (res.ok) {
          setUser(await res.json());
        } else {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            const token2 = localStorage.getItem("access_token");
            const res2 = await fetch("/api/auth/me", { headers: { Authorization: `Bearer ${token2}`, "Content-Type": "application/json" } });
            if (res2.ok) setUser(await res2.json());
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [refreshAccessToken]);

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated: !!user, login, logout, authedFetch }}>
      {children}
    </AuthContext.Provider>
  );
}
